import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  addCompanyFromAgent,
  companiesRef,
  editCompanyFromAgent
} from '../../api/firebase/firestore/company';
import { usersRef } from '../../api/firebase/firestore/user/index';
import { agentClientRegisterSchema } from '../../api/firebase/firestore/user/validation';
import {
  blackText,
  cardBorder,
  primaryButtonBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { agentClientRegisterSteps } from '../../constants/RegisterStep';
import { useIndustry } from '../../constants/Select';
import { select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { DocumentData } from 'firebase/firestore';

interface ClientRegisterBasicType {
  email: string;
  password: string;
  companyName: string;
  companyUrl: string;
  industry: number | undefined;
  industryOther: string | null;
  representativeName: string;
  department: string;
}

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb
};

interface Props {
  screen: number;
}

const RegisterClientBasicCard: React.FC<Props> = (): JSX.Element => {
  const { t } = useTranslation();
  const steps = agentClientRegisterSteps();
  const industryList = useIndustry();

  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [,] = useState<string>('');
  const navigate = useNavigate();
  const [ind, setInd] = useState<number>(0);

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ClientRegisterBasicType>({
    defaultValues: {
      industry: ind ?? 0
    },
    resolver: yupResolver(agentClientRegisterSchema)
  });

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === undefined ||
        '' ||
        watch('companyUrl') === undefined ||
        '' ||
        watch('industry') === undefined ||
        (watch('industry') === select.other &&
          Boolean(!watch('industryOther'))) ||
        Boolean(errors.companyName) ||
        Boolean(errors.companyUrl) ||
        Boolean(errors.industry) ||
        (watch('industry') === select.other && Boolean(errors.industryOther))
      );
    }
    return false;
  };

  useEffect(() => {
    if (watch('industry') !== select.other) {
      setValue('industryOther', null);
    }
  }, [watch('industry')]);

  const [companyData, setCompanyData] = useState<DocumentData>();

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, [companyData]);

  useEffect(() => {
    void (async () => {
      await init();
    })();
  }, []);

  const { currentUser } = useContext(AuthContext);
  const location = useLocation();

  const localState = location.state as { id: string };

  const init = async (): Promise<void> => {
    if (localState.id) {
      const company = doc(companiesRef, localState.id);
      const companySnap = await getDoc(company);
      setCompanyData(companySnap.data());

      reset({
        companyName: companySnap.data()?.name as string,
        companyUrl: companySnap.data()?.url as string,
        industry: companySnap.data()?.industry as number,
        industryOther: companySnap.data()?.industryOther as string
      });
      setInd(watch('industry') ?? 0);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          if (!localState?.id) {
            // 新規作成
            const companyId = await addCompanyFromAgent(
              watch,
              userSnap.data()?.companyId as string
            );
            navigate(`/agent/client/company`, {
              state: { companyId, editFlg: false }
            });
          } else {
            // 更新
            await editCompanyFromAgent(watch, localState.id);
            navigate(`/agent/client/company`, {
              state: { companyId: localState.id, editFlg: true }
            });
          }
        } else {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid>
          <Card sx={[styles.cardSize, styles.cardBorder]}>
            <CardContent sx={styles.cardContentPadding}>
              <CustomStepper steps={steps} activeStep={0} />
              <Typography sx={[styles.titleMb, styles.title]} component="div">
                {t('registerBasic.step1')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyName)}
                    helperText={errors.companyName?.message}
                    labelText={t('form.companyName')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('common.enbro')}
                  />
                )}
                control={control}
                name="companyName"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyUrl)}
                    helperText={errors.companyUrl?.message}
                    labelText={t('form.companyUrl')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('common.companyUrl')}
                    type="url"
                  />
                )}
                control={control}
                name="companyUrl"
              />
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    error={Boolean(errors.industry)}
                    helperText={errors.industry?.message}
                    blank
                    labelText={t('form.industry')}
                    requiredText={t('form.required')}
                    isRequired
                    list={industryList}
                    sx={{ width: '400px' }}
                    {...(watch('industry') === select.other && {
                      formMb: 'narrow'
                    })}
                  />
                )}
                control={control}
                name="industry"
              />
              {watch('industry') === select.other && (
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={
                        watch('industry') === select.other &&
                        Boolean(errors.industryOther)
                      }
                      helperText={errors.industryOther?.message}
                      isRequired={false}
                      placeholder={t('form.departmentOther')}
                      sx={{ marginTop: '0px' }}
                    />
                  )}
                  control={control}
                  name="industryOther"
                />
              )}

              <CardActions
                sx={{
                  '&.MuiCardActions-root': {
                    marginTop: '30px'
                  },
                  padding: 0
                }}
              >
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    sx={[primaryButtonBackground, { width: '100%' }]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: 15 }}
                    >
                      {t('agentClient.clientButtonText')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default RegisterClientBasicCard;
